import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl, { LngLatLike, MapMouseEvent } from 'mapbox-gl';
import turfAlong from '@turf/along';
import turfBearing from '@turf/bearing';
import turfBezier from '@turf/bezier-spline';
import { lineString, Feature, LineString, Position } from '@turf/helpers';
import turfLength from '@turf/length';
import { COLOR_MAP_ACTIVITY, COLOR_MAP_ROUTE } from '@alltrails/shared/denali/tokens';
import { atMapsToGeojson } from '@alltrails/maps/utils/legacyGeoJSONConversions';
import logFlyoverFinished from '@alltrails/analytics/events/logFlyoverFinished';
import FlyoverSource from '@alltrails/analytics/enums/FlyoverSource';
import { MILLISECONDS_IN_SECOND } from 'constants/DateConstants';
import useAnimationFrame from 'hooks/useAnimationFrame';
import useFeatures from 'hooks/useFeatures';
import useStateRef from 'hooks/useStateRef';
import useFlyoverControls from 'hooks/useFlyoverControls';
import FlyoverLoadingState from 'types/FlyoverLoadingState';
import FlyoverMapEventHandlers from 'types/FlyoverMapEventHandlers';
import FlyoverSettings from 'types/FlyoverSettings';
import { MapboxMapShim } from 'types/Map';
import {
  FLYOVER_LINE,
  FLYOVER_ANIM_DURATION_MS,
  addFlyoverEventListeners,
  computePositionValues,
  exitFlyoverMap,
  initializeFlyoverMap,
  removeFlyoverEventListeners
} from 'utils/flyoverHelpers';
import { StyleConfigKey } from 'utils/mapbox/map';
import { hideAtMap, showAtMap } from 'utils/mapbox/overlays/at_map';
import { updatePolylines } from 'utils/mapbox/overlays/polylines';
import { getGroupedSegments } from 'utils/elev/elev_pane_helpers';
import { requestElevations } from 'utils/requests/elevation_requests';
import logError from 'utils/logError';
import useMapLoadedEvents from './useMapLoadedEvents';

/**
 * Calculated from animating a marker over [Snow Lake Trail](https://www.alltrails.com/trail/us/washington/snow-lake-trail) in 40 seconds
 */
const SPEED = 0.25238996618; // km/s
const ACCEL_FACTOR = 0.005;
const DECEL_FACTOR = 0.05;
const MIN_BEARING_DIFF = 0.01;
const MAX_BEARING_VEL = 0.5;
const MAX_ZOOM = 17;
const MIN_ZOOM = 13;
const DEFAULT_PITCH = 65;
const AVG_TRAIL_LENGTH_KM = 15.77;
const STD_DEV_TRAIL_LENGTH_KM = 46.08;

export default function useFlyover(map: MapboxMapShim, atMap: any, initOnLoad?: boolean): FlyoverSettings {
  const [flyoverLoadingState, setFlyoverLoadingState, flyoverLoadingStateRef] = useStateRef<FlyoverLoadingState>('notStarted');
  const [disable3DOnExit, setDisable3DOnExit] = useState(true);
  const [originalLayer, setOriginalLayer] = useState<StyleConfigKey>();
  const newPolylineColorsFlag = __AT_DATA__.experiments?.['web-feed-polyline-color']?.value === 'on';

  // map references
  const marker = useRef<mapboxgl.Marker>(null);

  // route info
  const atMapGeojson = useMemo(() => atMapsToGeojson([atMap]), [atMap]);
  const pinRoute = useMemo(() => atMapGeojson.features[0]?.geometry.coordinates as Position[][], [atMapGeojson]);
  const [path, setPath] = useState<Feature<LineString, { [name: string]: any }>>();
  const [cameraPath, setCameraPath] = useState<Feature<LineString, { [name: string]: any }>>();
  const [pathDistance, setPathDistance] = useState<number>();
  const [cameraPathDistance, setCameraPathDistance] = useState<number>();
  const [segmentedElevGain, setSegmentedElevGain] = useState<number[]>([]);

  // animation state
  const [isComplete, setIsComplete] = useState(false);
  const [elevationGain, setElevationGain] = useState(0);
  const [distanceComplete, setDistanceComplete] = useState(0);
  const animationPhase = useRef(0);
  const calculatedDuration = useRef(0);
  const cameraBearing = useRef<number>(null);
  const cameraBearingVel = useRef(0); // max vel 0.5 degrees / frame
  const initialBearing = useRef(null);
  const initialZoom = useRef(null);
  const initialCameraPosition = useRef(null);
  const prevCoordinates = useRef(null);
  const prevCameraCoordinates = useRef(null);
  const prevElevationSeg = useRef(0);
  const startTime = useRef(0);
  const shouldResetTime = useRef(false);
  const timeElapsed = useRef(0);

  const features = useFeatures();
  const { didMapLoadEventsFire } = useMapLoadedEvents(map);
  const {
    isPaused,
    isPausedRef,
    shouldHideControls,
    hideControlsTimer,
    shouldResetHideControlsTimer,
    pauseFlyover,
    playFlyover,
    togglePauseFlyover,
    showControls,
    hideControls,
    isMouseDown,
    isTouching,
    interstitialEventHandlers: { keyDownHandler, mouseUpHandler },
    eventHandlers
  } = useFlyoverControls(atMap?.trailId, initOnLoad);

  useEffect(() => {
    // initialize static values based on the atMap
    if (features.includes('trail_flyover') && pinRoute && pinRoute[0].length > 1) {
      const pathString = lineString(pinRoute[0]);
      const pathDist = turfLength(pathString);
      const bezierPath = turfBezier(pathString, { resolution: 1000 });
      const startingBearing = turfBearing(turfAlong(bezierPath, 0).geometry.coordinates, turfAlong(bezierPath, 0.01).geometry.coordinates);

      setPath(pathString);
      setCameraPath(bezierPath);
      setPathDistance(pathDist);
      setCameraPathDistance(turfLength(bezierPath));

      calculatedDuration.current = (pathDist / SPEED) * MILLISECONDS_IN_SECOND;
      initialBearing.current = startingBearing;
      cameraBearing.current = startingBearing;
    }
  }, [map, features, pinRoute]);

  useEffect(() => {
    if (atMap) {
      const segments = getGroupedSegments(atMap)
        .map(group => group.segments)
        .flat();
      requestElevations(segments.map((seg: any) => seg.polyline.pointsData))
        .then(elevations => {
          setSegmentedElevGain(elevations.resampledElevations);
        })
        .catch(e => logError(`[Flyover] Elevation query failed: ${e}`));
    }
  }, [atMap]);

  /**
   * Responsible for updating the animation state on each call to `requestAnimationFrame`.
   * @param time current timestamp from when the first requestAnimationFrame was triggered
   */
  const flyoverFrame = (time: DOMHighResTimeStamp) => {
    if (shouldResetTime.current) {
      startTime.current = time - timeElapsed.current;
      shouldResetTime.current = false;

      hideControlsTimer.current = time;
    } else {
      timeElapsed.current = time - startTime.current;
    }

    if (shouldResetHideControlsTimer.current) {
      shouldResetHideControlsTimer.current = false;
      hideControlsTimer.current = time;
    }

    if (hideControlsTimer.current !== undefined && time - hideControlsTimer.current > MILLISECONDS_IN_SECOND * 3) {
      hideControls();
    }

    // animationPhase is a value between 0 and 1 that represents the progress of the animation
    animationPhase.current = timeElapsed.current / calculatedDuration.current;
    if (animationPhase.current >= 1) {
      // stop the animation
      pauseFlyover();

      setTimeout(() => {
        logFlyoverFinished({ source: initOnLoad ? FlyoverSource.TrailDetails : FlyoverSource.MapDetails, trail_id: atMap.trailId });
        showAtMap(map);
        setIsComplete(true);
        map.flyTo({ ...initialCameraPosition.current, speed: 0.3, curve: 3 });
        marker.current.getElement().style.visibility = 'hidden';
      }, 200);

      return;
    }

    if (isPausedRef.current) {
      return;
    }

    const { lngLat, position } = computePositionValues(path, pathDistance, animationPhase.current /* percentageComplete */, prevCoordinates.current);
    const { bearing: newCameraBearing, position: cameraPosition } = computePositionValues(
      cameraPath,
      cameraPathDistance,
      animationPhase.current,
      prevCameraCoordinates.current
    );

    setDistanceComplete(pathDistance * animationPhase.current * MILLISECONDS_IN_SECOND);
    const elevSegIndex = Math.round(segmentedElevGain.length * animationPhase.current);
    if (elevSegIndex > 0 && elevSegIndex > prevElevationSeg.current) {
      if (segmentedElevGain[elevSegIndex] > segmentedElevGain[elevSegIndex - 1]) {
        setElevationGain(elevGain => elevGain + (segmentedElevGain[elevSegIndex] - segmentedElevGain[elevSegIndex - 1]));
      }
      prevElevationSeg.current += 1;
    }

    // Update the marker location
    marker.current.setLngLat(lngLat);

    // Reduce the visible length of the line by using a line-gradient to cutoff the line
    updatePolylines(
      map,
      FLYOVER_LINE,
      'line-gradient',
      ['step', ['line-progress'], newPolylineColorsFlag ? COLOR_MAP_ROUTE : 'red', animationPhase.current, 'rgba(0, 0, 0, 0)'],
      ['step', ['line-progress'], newPolylineColorsFlag ? COLOR_MAP_ACTIVITY : 'red', animationPhase.current, 'rgba(0, 0, 0, 0)']
    );

    /* camera logic */
    if (!isMouseDown.current && !isTouching.current) {
      // determine the minimum angle to rotate the camera to the new bearing
      let bearingDiff = (newCameraBearing - cameraBearing.current) % 360;
      if (bearingDiff < -180) {
        bearingDiff += 360;
      } else if (bearingDiff > 180) {
        bearingDiff -= 360;
      }

      if (bearingDiff > 0 && Math.abs(bearingDiff) > MIN_BEARING_DIFF) {
        cameraBearingVel.current += cameraBearingVel.current > 0 ? ACCEL_FACTOR : DECEL_FACTOR;
      } else if (bearingDiff < 0 && Math.abs(bearingDiff) > MIN_BEARING_DIFF) {
        cameraBearingVel.current -= cameraBearingVel.current < 0 ? ACCEL_FACTOR : DECEL_FACTOR;
      } else {
        cameraBearingVel.current = 0;
      }
      cameraBearingVel.current = Math.max(Math.min(cameraBearingVel.current, MAX_BEARING_VEL), -MAX_BEARING_VEL);
      cameraBearing.current += cameraBearingVel.current;

      map.jumpTo({
        center: cameraPosition,
        bearing: cameraBearing.current
      });
    }

    prevCoordinates.current = position;
    prevCameraCoordinates.current = cameraPosition;
  };

  // memoize to avoid extra calls to `cancelAnimationFrame` due to re-rendering of the compass rotation
  const animationDeps = useMemo(() => [], []);
  useAnimationFrame(flyoverFrame, isPaused, undefined /* onAnimationCancelled */, animationDeps);

  /**
   * Toggles between playing & pausing the animation.
   *
   * If resuming, resets the camera to the most recent position along the camera path before resuming.
   */
  const togglePlayPause = useCallback(() => {
    const { lng, lat } = map.getCenter();
    const resume = () => {
      shouldResetTime.current = true;
      playFlyover();
    };

    const resetCameraAndPlay = () => {
      if (lng !== prevCameraCoordinates.current?.[0] || lat !== prevCameraCoordinates.current?.[1] || map.getBearing() !== cameraBearing.current) {
        // unfortunately calling map.flyTo synchronously doesn't work well with default click-and-drag behavior
        setTimeout(() => {
          map.flyTo({
            center: prevCameraCoordinates.current,
            bearing: cameraBearing.current,
            speed: 0.3,
            curve: 2,
            essential: true
          });

          map.once('moveend', resume);
        }, 0);
      } else {
        resume();
      }
    };

    togglePauseFlyover(pauseFlyover, resetCameraAndPlay);
  }, [map, pauseFlyover, playFlyover, togglePauseFlyover]);

  const onKeyDown = useCallback((e: KeyboardEvent) => keyDownHandler(e, map, togglePlayPause), [keyDownHandler, map, togglePlayPause]);
  const onMouseUp = useCallback((e: MapMouseEvent) => mouseUpHandler(e, togglePlayPause), [mouseUpHandler, togglePlayPause]);
  const flyoverHandlers: FlyoverMapEventHandlers = { ...eventHandlers, onKeyDown, onMouseUp };

  useEffect(() => {
    // remove event listeners on complete to restore normal map functionality
    if (isComplete) {
      removeFlyoverEventListeners(map, pauseFlyover, flyoverHandlers);
    }
  }, [eventHandlers, isComplete, map, pauseFlyover]);

  /**
   * Initialize flyover by
   * 1) Enabling 3D
   * 2) Removing the existing polyline
   * 3) Drawing the line and marker that will be animated
   * @param enabled3D whether 3D is enabled at the time of initializing
   * @param enable3D a function to enable 3D view
   * @param setLayer a function to set a specified map layer
   * @param currentLayer the current layer at the time of initializing
   * @param onMapLoaded a callback fired when the correct layer & 3D view is loaded
   */
  const initFlyover = useCallback(
    (
      enabled3D: boolean,
      enable3D: (disablePitchAdjustment?: boolean) => void,
      setLayer: (layer: string) => void,
      currentLayer: StyleConfigKey,
      onMapLoaded?: () => void
    ) => {
      if (!features.includes('trail_flyover') || !map || !atMap || !didMapLoadEventsFire) {
        return;
      }

      const isExiting = flyoverLoadingStateRef.current === 'exiting';
      setFlyoverLoadingState('loading');
      if (!isExiting) {
        setOriginalLayer(currentLayer);
      } else {
        map.stop();
      }

      const shouldSwitchLayers = currentLayer !== 'alltrailsSatellite';
      if (shouldSwitchLayers) {
        setLayer('alltrailsSatellite');
      }

      const initMap = () => {
        const { marker: markerPopup } = initializeFlyoverMap(map, atMapGeojson);

        marker.current = markerPopup;
        marker.current.setLngLat(pinRoute[0][0] as LngLatLike).addTo(map);

        // use z-scale normalization to find intial camera zoom from trail distance
        const zScale = (pathDistance - AVG_TRAIL_LENGTH_KM) / STD_DEV_TRAIL_LENGTH_KM;
        const normalizedZoom = Math.cbrt(zScale) + (MAX_ZOOM - (MAX_ZOOM - MIN_ZOOM) / 2);
        const clampedZoom = Math.min(Math.max(normalizedZoom, MIN_ZOOM), MAX_ZOOM);
        const clampedZoomInversed = MAX_ZOOM - (clampedZoom - MIN_ZOOM);
        initialZoom.current = clampedZoomInversed;

        map.flyTo({
          center: pinRoute[0][0] as LngLatLike,
          zoom: clampedZoomInversed,
          bearing: cameraBearing.current,
          pitch: DEFAULT_PITCH,
          speed: 0.3,
          curve: 3,
          essential: true
        });

        let flyoverStarted = false;
        const startFlyover = () => {
          if (!flyoverStarted) {
            flyoverStarted = true;
            shouldResetTime.current = true;

            addFlyoverEventListeners(map, pauseFlyover, flyoverHandlers);
            setFlyoverLoadingState('done');
            playFlyover();
            showControls();
          }
        };

        map.once('moveend', () => {
          // after the map pan, wait until either the map finishes loading or 1s has passed
          map.once('idle', startFlyover);
          setTimeout(startFlyover, MILLISECONDS_IN_SECOND);
        });
      };

      const onMapReady = () => {
        onMapLoaded();
        initialCameraPosition.current = {
          bearing: map.getBearing(),
          center: map.getCenter(),
          pitch: map.getPitch(),
          zoom: map.getZoom()
        };
        setTimeout(initMap, FLYOVER_ANIM_DURATION_MS * 1.5 /* add a small buffer for animations to complete */);
      };

      const set3D = () => {
        if (!enabled3D) {
          enable3D(true /* disablePitchAdjustment */);
          if (!isExiting) {
            setDisable3DOnExit(true);
          }
          map.once('idle', onMapReady);
        } else {
          if (!isExiting) {
            setDisable3DOnExit(false);
          }
          onMapReady();
        }
      };

      if (shouldSwitchLayers) {
        map.once('idle', set3D);
      } else {
        set3D();
      }
    },
    [features, map, atMap, didMapLoadEventsFire, atMapGeojson, pinRoute, pathDistance, showControls, playFlyover, pauseFlyover]
  );

  /**
   * Pause and reset the animation, resetting the marker and polyline.
   */
  const resetAnimation = () => {
    pauseFlyover();
    setIsComplete(false);
    setElevationGain(0);
    animationPhase.current = 0;
    cameraBearing.current = initialBearing.current;
    cameraBearingVel.current = 0;
    prevCoordinates.current = null;
    prevCameraCoordinates.current = null;
    prevElevationSeg.current = 0;
    timeElapsed.current = 0;
    startTime.current = 0;
    marker.current?.setLngLat(pinRoute[0][0] as LngLatLike);
    updatePolylines(map, FLYOVER_LINE, 'line-gradient', [
      'step',
      ['line-progress'],
      newPolylineColorsFlag ? COLOR_MAP_ROUTE : 'red',
      0,
      'rgba(255, 0, 0, 0)'
    ]);
  };

  /**
   * Replays the animation from the beginning.
   */
  const replayAnimation = () => {
    hideAtMap(map);
    map.stop();
    marker.current.getElement().style.visibility = 'visible';
    resetAnimation();
    map.flyTo({
      center: pinRoute[0][0] as LngLatLike,
      bearing: initialBearing.current,
      zoom: initialZoom.current,
      pitch: DEFAULT_PITCH,
      speed: 0.3,
      curve: 3,
      essential: true
    });

    map.once('idle', () => {
      shouldResetTime.current = true;
      setTimeout(() => {
        playFlyover();
        addFlyoverEventListeners(map, pauseFlyover, flyoverHandlers);
      }, 500);
    });
  };

  /**
   * Exits the flyover experience by resetting internal values & removing extra event listeners.
   * Updates the loading state accordingly.
   * @param disable3D a function disable the 3D terrain
   * @param setLayer a function to change map layers
   */
  const exitFlyover = (disable3D: () => void, setLayer: (layer: string) => void) => {
    setFlyoverLoadingState('exiting');
    resetAnimation();
    removeFlyoverEventListeners(map, pauseFlyover, flyoverHandlers);
    marker.current?.remove();
    marker.current = null;

    exitFlyoverMap(map, atMapGeojson);

    const returnMap = () => {
      if (flyoverLoadingStateRef.current !== 'exiting') {
        return;
      }

      setLayer(originalLayer);
      map.once('idle', () => {
        setFlyoverLoadingState('notStarted');
      });
    };

    map.once('idle', () => {
      if (flyoverLoadingStateRef.current !== 'exiting') {
        return;
      }

      if (disable3DOnExit) {
        disable3D();
        map.once('idle', returnMap);
      } else {
        returnMap();
      }
    });
  };

  return {
    didMapLoadEventsFire,
    distanceCompleteMeters: distanceComplete,
    elevationGainMeters: elevationGain,
    exitFlyover,
    flyoverLoadingState,
    initFlyover,
    isComplete,
    isPaused,
    percentComplete: animationPhase.current,
    resetAnimation,
    replayAnimation,
    shouldHideControls,
    togglePauseFlyover: togglePlayPause
  };
}
